import { Controller } from "@hotwired/stimulus"
import { put, post } from '@rails/request.js'

export default class extends Controller {
  static targets = ["all", "one", "operator", "hidden", "search", "container", "select", "actions"];

  connect() {
    if (this.hasAllTarget) this.allTarget.checked = this.everyChecked();

    if (this.hasSearchTarget) {
      this.searchTarget.addEventListener("input", this.filterResults.bind(this));
    }

    this.prepareOperators();
    this.updateOperators();
  }

  filterResults(event) {
    const query = event.target.value.toLowerCase().trim();
    const rows = this.containerTarget.querySelectorAll("tr");
    
    // if (query) {
    //   this.allTarget.disabled = true
    // } else {
    //   this.allTarget.disabled = false
    // }

    function highlightLink(cell, query) {
      const link = cell.querySelector("a")

      const originalText = link.textContent;
      const lowerCaseText = originalText.toLowerCase();

      link.innerHTML = originalText;

      if (lowerCaseText.includes(query) && query.length > 0) {
        const highlightedText = highlightText(originalText, query);
        link.innerHTML = highlightedText;
      }
    }

    function highlightText(text, query) {
      const regex = new RegExp(`(${query})`, 'gi');
      return text.replace(regex, '<span class="bg-orange-400 text-black">$1</span>');
    }
    
    function hideRows(row) {
      row.classList.remove("hidden");
      const pipelineRules = row.nextElementSibling;
      if (pipelineRules && pipelineRules.classList.contains("rules")) {
        pipelineRules.classList.remove("hidden");
        const conversionEventRules = pipelineRules.nextElementSibling;
        if (conversionEventRules && conversionEventRules.classList.contains("rules")) {
          conversionEventRules.classList.remove("hidden");
        }
      }
    }

    function showRows(row) {
      row.classList.add("hidden");
      const pipelineRules = row.nextElementSibling;
      if (pipelineRules && pipelineRules.classList.contains("rules")) {
        pipelineRules.classList.add("hidden");
        const conversionEventRules = pipelineRules.nextElementSibling;
        if (conversionEventRules && conversionEventRules.classList.contains("rules")) {
          conversionEventRules.classList.add("hidden");
        }
      }
    }

    rows.forEach(row => {      
      const cell = row.querySelector(".name");

      if (cell) {
        highlightLink(cell, query);

        const text = cell.textContent.toLowerCase();
        // Match the destination id too (carried on the row as data-destination-id)
        // so operators can filter by id, not just name.
        const destinationId = (row.dataset.destinationId || "").toLowerCase();
        if (text.includes(query) || destinationId.includes(query)) {
          hideRows(row);
        } else {
          showRows(row);
        }
      }
    })
  }

  toggleAll() {
    if (!this.hasAllTarget) return;
    let visibleTargets = this.oneTargets.filter(x => !x.classList.contains("hidden"));
    visibleTargets.forEach(x => x.checked = this.allTarget.checked);
    this.updateOperators();
  }

  toggleOne() {
    if (this.hasAllTarget) this.allTarget.checked = this.everyChecked();
    this.updateOperators();
  }

  async apply(event) {
    const target = event.currentTarget;
    const actionsDiv = target.closest('[data-group-operations-target="actions"]');
    const siblingSelect = this.selectTargets.find(s => actionsDiv.contains(s));
    const operation = target.dataset.operation || (siblingSelect && siblingSelect.value);

    if (!operation) return;

    this.setLoading(true);

    const key = target.dataset.key || "ids";
    const body = {
      operation: operation,
      [key]: this.selectedValues(),
      ...this.hiddenFields()
    };

    let response;

    try {
      switch (target.dataset.method) {
        case "post":
          response = await post(target.dataset.url, { body, responseKind: 'turbo-stream' });
          break;
        case "put":
          response = await put(target.dataset.url, { body, responseKind: 'turbo-stream' });
          break;
        case "get":
          let url = new URL(target.dataset.url, window.location.href);
          let params = new URLSearchParams(url.search);

          params.append(key, this.selectedValues().join(','));

          for (const [key, value] of Object.entries(this.hiddenFields())) {
            params.append(key, value);
          }

          url.search = params;

          return Turbo.visit(url);
      }

      if (response && response.response && response.response.redirected) {
        Turbo.visit(response.response.url);
      }
    } catch {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.operatorTargets.forEach(o => {
      o.disabled = loading;
      if (loading) {
        o.dataset.originalText = o.innerHTML;
        o.innerHTML = "Applying...";
      } else if (o.dataset.originalText) {
        o.innerHTML = o.dataset.originalText;
      }
    });

    this.selectTargets.forEach(s => {
      s.disabled = loading;
    });
  }

  someChecked() {
    return this.oneTargets.some(x => x.checked === true);
  }

  everyChecked() {
    return this.oneTargets.every(x => x.checked === true);
  }

  // Selected values are values that are both visible and checked
  selectedValues() {
    return this.oneTargets.filter(x => {
      return x.checked && !x.closest('tr').classList.contains('hidden');
    }).map(x => x.value);
  }

  prepareOperators() {
    this.operatorTargets.forEach(o => {
      if (!o.dataset.everyText) {
        o.dataset.everyText = o.innerHTML.replace("*", "All");
      }

      if (!o.dataset.someText) {
        o.dataset.someText = o.innerHTML.replace("*", "Selected");
      }
    });
  }

  updateOperators() {
    const hasSelection = this.someChecked();

    this.actionsTargets.forEach(a => {
      a.classList.toggle("opacity-50", !hasSelection);
      a.classList.toggle("pointer-events-none", !hasSelection);
    });

    this.selectTargets.forEach(s => {
      s.disabled = !hasSelection;
    });

    if (this.everyChecked()) {
      this.operatorTargets.forEach(o => {
        o.innerHTML = o.dataset.everyText;
        o.disabled = false;
      });
    }
    else if (hasSelection) {
      this.operatorTargets.forEach(o => {
        o.innerHTML = o.dataset.someText;
        o.disabled = false;
      });
    }
    else {
      this.operatorTargets.forEach(o => {
        o.innerHTML = o.dataset.someText;
        o.disabled = true;
      });
    }

  }

  hiddenFields() {
    let hidden = { };

    this.hiddenTargets.forEach(h => hidden[h.name] = h.value)

    return hidden;
  }
}
